import type { RoomLayoutSpot } from './RoomLayoutMap.types';
import { SpotStatus } from './RoomLayoutMap.types';
import { getSpotKey, resolveSpotKeyInLayout } from './spotKey';
import { isSelectionReservedByOthers } from './spotSelectionCleanup';

/**
 * Multi-slot spot selection for "member books with friends" (BOOKING-4174).
 * Slot 0 is always the member ("Your Spot"); slots 1..guestsCount are Friend 1..N.
 * Pure state + reducers so RoomLayoutMap stays a thin orchestrator.
 */
export type FriendSpotSelectionState = {
  /** length = guestsCount + 1; index 0 = member, i>0 = Friend i. null = empty slot. */
  selections: (string | null)[];
  activeSlotIndex: number;
};

/** Chip data for FriendSpotSlots: label ("Your Spot" / "Friend 2") + assigned spot display name. */
export type FriendSlotChip = {
  label: string;
  spotLabel: string | null;
};

export type FriendSlotStrings = {
  YOUR_SPOT: string;
  FRIEND_SLOT: string;
};

const MEMBER_SLOT_INDEX = 0;

/** Spots indexed by key, so slot lookups stay O(1) instead of scanning the layout per slot. */
function indexSpotsByKey(spots: RoomLayoutSpot[]): Map<string, RoomLayoutSpot> {
  return new Map(spots.map((spot) => [getSpotKey(spot), spot]));
}

/** Empty state with guestsCount + 1 slots, member slot active. Negative/NaN guest counts clamp to 0. */
export function createFriendSpotSelectionState(guestsCount: number): FriendSpotSelectionState {
  const guests = Number.isFinite(guestsCount) ? Math.max(0, Math.floor(guestsCount)) : 0;
  return {
    selections: Array.from({ length: guests + 1 }, () => null),
    activeSlotIndex: 0
  };
}

/**
 * Tap reducer for the manual-selection model (AC9): the active slot never moves on a spot tap —
 * only tapping a participant tab changes which slot is active.
 * - spot already assigned to a slot → clear that slot (deselect), keeping the active slot as-is;
 * - otherwise assign the spot to the active slot, overwriting whatever it held.
 * Every tap changes something (re-tapping a slot's own spot takes the deselect arm), so the result
 * is always a fresh state.
 */
export function pressSpotForFriendSelection(
  state: FriendSpotSelectionState,
  spotKey: string
): FriendSpotSelectionState {
  const ownerIndex = state.selections.indexOf(spotKey);
  const selections = state.selections.slice();
  if (ownerIndex !== -1) {
    selections[ownerIndex] = null;
  } else {
    selections[state.activeSlotIndex] = spotKey;
  }
  return { selections, activeSlotIndex: state.activeSlotIndex };
}

/**
 * Drop selections whose spot became BLOCKED, appears in reservedSpotKeys, or disappeared from the
 * layout — the multi-slot version of the single-selection cleanup effect in RoomLayoutMap. Like
 * that effect, a slot-0 selection matching `assignedSpotKey` (the member's own assigned spot) is
 * exempt from the reserved check. The active slot never moves (AC9): clearing is triggered by other
 * members booking, not by the user, so it must not pull the active tab out from under them. The
 * cleared chip reads empty and the user taps it if they want it back. Returns the same reference
 * when nothing was cleared.
 */
export function clearBlockedFriendSelections(
  state: FriendSpotSelectionState,
  spots: RoomLayoutSpot[],
  reservedSpotKeys: string[] | undefined,
  assignedSpotKey?: string | null
): FriendSpotSelectionState {
  const assigned = assignedSpotKey ?? null;
  // An empty spots array means the layout hasn't loaded yet, not that every spot vanished.
  const layoutIsLoaded = spots.length > 0;
  const spotsByKey = indexSpotsByKey(spots);
  const isSelectionBlocked = (key: string, index: number): boolean => {
    const spot = spotsByKey.get(key);
    // Slot 0 is the member, so a friend slot holding the same key never inherits the exemption.
    const exemptKey = index === MEMBER_SLOT_INDEX ? assigned : null;
    const isReserved = isSelectionReservedByOthers(spot, key, reservedSpotKeys, exemptKey);
    if (spot === undefined) {
      // Gone from a loaded layout: the slot would keep counting as filled while the chip reads
      // empty, so drop it. With no layout yet, only an explicit reserved key clears the slot.
      return layoutIsLoaded || isReserved;
    }
    return isReserved || spot.status === SpotStatus.BLOCKED;
  };
  const clearedIndexes = state.selections.flatMap((key, index) =>
    key !== null && isSelectionBlocked(key, index) ? [index] : []
  );
  if (clearedIndexes.length === 0) return state;
  const selections = state.selections.map((key, index) =>
    clearedIndexes.includes(index) ? null : key
  );
  return { selections, activeSlotIndex: state.activeSlotIndex };
}

export function findFriendSlotsForSpotNames(
  state: FriendSpotSelectionState,
  spots: RoomLayoutSpot[],
  spotNames: readonly string[]
): number[] {
  const indexes = new Set<number>();
  spotNames.forEach((name) => {
    const key = resolveSpotKeyInLayout(spots, name) ?? name.trim();
    if (key === '') return;
    const index = state.selections.indexOf(key);
    if (index !== -1) indexes.add(index);
  });
  return [...indexes].sort((a, b) => a - b);
}

/**
 * The one case where the active slot moves without a tab tap, deliberately breaking the AC9
 * invariant the reducers above keep: here the member is the one retrying, so the point is to put
 * them on the slot they have to fix.
 */
export function clearFriendSlotsForError(
  state: FriendSpotSelectionState,
  slotIndexes: readonly number[]
): FriendSpotSelectionState {
  if (slotIndexes.length === 0) return state;
  const selections = state.selections.map((key, index) =>
    slotIndexes.includes(index) ? null : key
  );
  const first = Math.min(...slotIndexes);
  const activeSlotIndex = first >= 0 && first < selections.length ? first : state.activeSlotIndex;
  return { selections, activeSlotIndex };
}

export function areSpotNameSetsEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedB = b.map((name) => name.trim()).sort();
  return a
    .map((name) => name.trim())
    .sort()
    .every((name, index) => name === sortedB[index]);
}

/** Chip labels for FriendSpotSlots. spotLabel uses the spot's display name (spot_name). */
export function buildFriendSlotChips(
  state: FriendSpotSelectionState,
  spots: RoomLayoutSpot[],
  strings: FriendSlotStrings
): FriendSlotChip[] {
  const spotsByKey = indexSpotsByKey(spots);
  return state.selections.map((key, index) => {
    const spot = key === null ? undefined : spotsByKey.get(key);
    return {
      label:
        index === MEMBER_SLOT_INDEX ? strings.YOUR_SPOT : `${strings.FRIEND_SLOT} ${String(index)}`,
      spotLabel: spot?.spot_name ?? null
    };
  });
}

/** Selections resolved to full spots for the onFriendsSpotSelectionChange callback. */
export function resolveFriendSelectionSpots(
  state: FriendSpotSelectionState,
  spots: RoomLayoutSpot[]
): (RoomLayoutSpot | null)[] {
  const spotsByKey = indexSpotsByKey(spots);
  return state.selections.map((key) => (key === null ? null : (spotsByKey.get(key) ?? null)));
}

/** Non-null selected keys, for RoomLayoutGrid multi-highlight. */
export function getSelectedFriendSpotKeys(state: FriendSpotSelectionState): string[] {
  return state.selections.filter((key): key is string => key !== null);
}
